import { useCallback, useState } from "react";
import { ArrowDown, ArrowUpRight, Play } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { filmography } from "@/data/content";
import { Eyebrow, Reveal, SectionTitle } from "./Reveal";
import { ReelModal, ReelStrip } from "./Showreel";
import { ActingAlbum, ResumeDownload } from "./ActingAlbum";

const Featured = ({ onPlay }) => (
  <Reveal className="relative group overflow-hidden" data-testid="acting-featured-crooks">
    <div className="relative aspect-[4/5] sm:aspect-[16/10] lg:aspect-[21/9] overflow-hidden">
      <img src="/img/acting-featured.jpg" alt="Mimi Tao as Achara on the CROOKS Season 2 set" style={{ objectPosition: "35% 25%" }} className="h-full w-full object-cover scale-105 group-hover:scale-100 transition-transform duration-[1800ms] ease-out" />
      <div className="hidden lg:block absolute inset-0 bg-gradient-to-l from-black/80 via-black/35 to-transparent" />
    </div>
    <div className="relative lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2 bg-[var(--ink)] lg:bg-transparent p-6 sm:p-10 lg:p-12 flex flex-col justify-end lg:items-end lg:text-right text-white">
      <div className="flex items-center gap-3 mb-4">
        <span className="bg-[#E50914] text-white text-[11px] font-semibold tracking-[0.2em] px-2.5 py-1">NETFLIX</span>
        <span className="text-xs uppercase tracking-[0.25em] text-white/85">Series · 2026</span>
      </div>
      <div className="flex flex-col lg:items-end gap-6">
        <div>
          <h3 className="font-display text-6xl sm:text-8xl lg:text-9xl leading-[0.85] tracking-tight">CROOKS</h3>
          <p className="font-display italic text-2xl sm:text-3xl text-[var(--gold-soft)] mt-2">Season Two</p>
          <p className="mt-4 text-white/85 text-sm sm:text-base">Mimi Tao as <span className="text-white">Achara</span></p>
        </div>
        <button data-testid="acting-showreel-btn" onClick={onPlay}
          className="self-start lg:self-end group/btn flex items-center gap-4 text-[12px] uppercase tracking-[0.22em]">
          <span className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border border-white/70 flex items-center justify-center group-hover/btn:bg-white group-hover/btn:border-white group-hover/btn:text-black transition-colors duration-500">
            <Play size={20} className="ml-1" />
          </span>
          Watch showreel
        </button>
      </div>
    </div>
  </Reveal>
);

export const Acting = () => {
  const [reel, setReel] = useState(null);
  const close = useCallback(() => setReel(null), []);
  return (
    <section id="acting" data-testid="acting-section" className="max-w-[1500px] mx-auto px-5 sm:px-10 pt-36 sm:pt-44 pb-28">
      <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <Eyebrow className="mb-6">01 — Acting</Eyebrow>
          <SectionTitle>On <em className="text-[var(--gold)]">screen</em>.</SectionTitle>
        </div>
        <div className="flex flex-col md:items-end gap-5">
          <p className="max-w-sm text-stone-600 font-light leading-relaxed md:text-right">Restraint, precision, and the courage to hold a silence. The work, in order.</p>
          <a data-testid="acting-header-resume-btn" href="/mimi-tao-resume.pdf" download="Mimi-Tao-Resume.pdf"
            className="self-start md:self-auto inline-flex items-center gap-2 rounded-full bg-[var(--ink)] px-6 py-3 text-[12px] uppercase tracking-[0.22em] text-white hover:bg-[var(--gold)] transition-colors duration-500">
            Résumé <ArrowDown size={14} />
          </a>
        </div>
      </Reveal>
      <Featured onPlay={() => setReel(0)} />
      <ReelStrip onOpen={setReel} cols="sm:grid-cols-2 lg:grid-cols-3" />
      <div className="mt-16" data-testid="filmography-list">
        {filmography.map((f, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div data-testid={`filmography-row-${i}`}
              className="group grid grid-cols-12 gap-4 items-baseline py-7 border-b border-black/10 hover:bg-[#f1e6da]/60 transition-colors">
              <span className="col-span-3 sm:col-span-2 text-sm text-stone-500 tracking-widest">{f.year}</span>
              <span className="col-span-9 sm:col-span-5">
                <span className="block font-display text-2xl sm:text-4xl group-hover:translate-x-3 group-hover:text-[var(--gold)] transition-[transform,color] duration-500">{f.title}</span>
                {f.note && (
                  <a href={f.noteHref} target="_blank" rel="noreferrer" data-testid={`filmography-note-${i}`}
                    className="mt-2 inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.2em] text-[var(--gold)] link-line">
                    {f.note} <ArrowUpRight size={13} />
                  </a>
                )}
              </span>
              <span className="col-span-6 col-start-4 sm:col-start-auto sm:col-span-3 text-sm text-stone-600">{f.role}</span>
              <span className="col-span-3 sm:col-span-2 text-right text-xs uppercase tracking-[0.2em] text-stone-500">{f.where}</span>
            </div>
          </Reveal>
        ))}
      </div>
      <ActingAlbum />
      <Reveal className="mt-24 border-t border-black/10 pt-12 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <Eyebrow className="mb-3">For casting</Eyebrow>
          <p className="font-display text-2xl sm:text-4xl">Credits, stats, skills <em className="text-[var(--gold)]">&</em> digitals.</p>
        </div>
        <ResumeDownload />
      </Reveal>
      <AnimatePresence>{reel !== null && <ReelModal start={reel} onClose={close} />}</AnimatePresence>
    </section>
  );
};
